import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog';
import {
  FileText,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  ShieldCheck,
  Award,
  UserCheck,
  Image,
  Check,
  Ban,
  Loader2,
  Calendar,
  AlertTriangle,
  FileCheck,
} from 'lucide-react';
import { adminApi, filesApi, httpClient, type DoctorDocument } from '@/api';
import { useToast } from '@/components/ui/use-toast';

export interface DoctorForDocuments {
  id: string;
  name: string;
  email?: string;
  crm: string;
  crmState?: string;
  crmUf?: string;
  status: string;
  specialty?: string;
}

export interface DoctorDocumentsModalProps {
  doctor: DoctorForDocuments | null;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const DOCUMENT_TYPE_CONFIG: Record<
  string,
  { label: string; description: string; icon: typeof FileText }
> = {
  CRM: {
    label: 'Carteira do CRM',
    description: 'Cédula de identidade médica emitida pelo CRM/CFM',
    icon: UserCheck,
  },
  DIPLOMA: {
    label: 'Diploma de Medicina',
    description: 'Diploma de graduação em Medicina frente e verso',
    icon: Award,
  },
  IDENTITY: {
    label: 'Documento com Foto (RG/CNH)',
    description: 'Documento oficial de identificação com foto',
    icon: FileText,
  },
  RQE: {
    label: 'Registro de Qualificação de Especialista (RQE)',
    description: 'Comprovante do RQE emitido pelo CRM',
    icon: FileCheck,
  },
  PHOTO: {
    label: 'Foto de Perfil',
    description: 'Foto recente para perfil do profissional',
    icon: Image,
  },
  SELFIE: {
    label: 'Selfie com Documento',
    description: 'Foto do médico segurando o documento de identificação',
    icon: UserCheck,
  },
  CERTIFICATE: {
    label: 'Certificado de Especialidade',
    description: 'Certificado de conclusão de residência ou título',
    icon: Award,
  },
};

const COMMON_REJECTION_REASONS = [
  'Documento ilegível ou com baixa resolução',
  'Dados divergentes do cadastro (CRM ou Nome)',
  'Documento com validade expirada',
  'Falta frente e/ou verso do documento',
  'Documento incompleto ou cortado',
];

export function DoctorDocumentsModal({
  doctor,
  open,
  onClose,
  onSuccess,
}: DoctorDocumentsModalProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [rejectingDocId, setRejectingDocId] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [activeMutatingId, setActiveMutatingId] = useState<string | null>(null);

  const doctorId = doctor?.id;

  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['admin', 'doctor', doctorId, 'documents'],
    queryFn: () => adminApi.getDoctorDocuments(doctorId!),
    enabled: !!doctorId && open,
  });

  const verifyMutation = useMutation({
    mutationFn: async ({
      documentId,
      status,
      reason,
    }: {
      documentId: string;
      status: 'APPROVED' | 'REJECTED';
      reason?: string;
    }) => {
      if (!doctorId) throw new Error('Doctor ID não informado');
      setActiveMutatingId(documentId);
      return adminApi.verifyDoctorDocument(doctorId, documentId, {
        status,
        rejectionReason: reason,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'doctor', doctorId, 'documents'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'doctors'] });
      toast({
        title: variables.status === 'APPROVED' ? 'Documento aprovado' : 'Documento rejeitado',
        description:
          variables.status === 'APPROVED'
            ? 'O documento foi validado com sucesso.'
            : 'O documento foi marcado como rejeitado com o motivo informado.',
      });
      setRejectingDocId(null);
      setRejectionReason('');
      onSuccess?.();
    },
    onError: (err: any) => {
      toast({
        title: 'Erro ao atualizar documento',
        description: err?.response?.data?.message || err?.message || 'Tente novamente.',
        variant: 'error',
      });
    },
    onSettled: () => {
      setActiveMutatingId(null);
    },
  });

  const [isRejectingDoctor, setIsRejectingDoctor] = useState(false);
  const [doctorRejectReason, setDoctorRejectReason] = useState('');

  const approveDoctorMutation = useMutation({
    mutationFn: async () => {
      if (!doctorId) throw new Error('Doctor ID não informado');
      return httpClient.patch(`/admin/doctors/${doctorId}/approve`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'doctor', doctorId, 'documents'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'doctors'] });
      toast({
        title: 'Médico aprovado!',
        description: 'O cadastro médico foi validado e a conta foi ativada com sucesso.',
      });
      onSuccess?.();
    },
    onError: (err: any) => {
      toast({
        title: 'Erro ao aprovar médico',
        description: err?.response?.data?.message || err?.message || 'Tente novamente.',
        variant: 'error',
      });
    },
  });

  const rejectDoctorMutation = useMutation({
    mutationFn: async (reason?: string) => {
      if (!doctorId) throw new Error('Doctor ID não informado');
      return httpClient.patch(`/admin/doctors/${doctorId}/reject`, { reason });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'doctor', doctorId, 'documents'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'doctors'] });
      toast({
        title: 'Cadastro médico rejeitado',
        description: 'O status foi atualizado e o profissional foi notificado.',
      });
      setIsRejectingDoctor(false);
      setDoctorRejectReason('');
      onSuccess?.();
    },
    onError: (err: any) => {
      toast({
        title: 'Erro ao rejeitar médico',
        description: err?.response?.data?.message || err?.message || 'Tente novamente.',
        variant: 'error',
      });
    },
  });

  if (!doctor) return null;

  // Extract CRM and UF clearly
  const rawCrm = doctor.crm || data?.doctor?.crm || '';
  const crmNumber = rawCrm.includes('/') ? rawCrm.split('/')[1] : rawCrm;
  const crmUf =
    doctor.crmState ||
    doctor.crmUf ||
    data?.doctor?.crmState ||
    (rawCrm.includes('/') ? rawCrm.split('/')[0] : 'SP');

  const verification = data?.verification;
  const documents: DoctorDocument[] = data?.documents || [];

  const hasCrmApproved = documents.some((d) => d.type === 'CRM' && d.status === 'APPROVED');
  const hasDiplomaApproved = documents.some((d) => d.type === 'DIPLOMA' && d.status === 'APPROVED');
  const hasIdentityApproved = documents.some((d) => d.type === 'IDENTITY' && d.status === 'APPROVED');
  const approvedEssentialCount = [hasCrmApproved, hasDiplomaApproved, hasIdentityApproved].filter(Boolean).length;

  const handleApprove = (docId: string) => {
    verifyMutation.mutate({ documentId: docId, status: 'APPROVED' });
  };

  const handleStartReject = (docId: string) => {
    setRejectingDocId(docId);
    setRejectionReason('');
  };

  const handleCancelReject = () => {
    setRejectingDocId(null);
    setRejectionReason('');
  };

  const handleConfirmReject = (docId: string) => {
    if (!rejectionReason.trim()) {
      toast({
        title: 'Motivo obrigatório',
        description: 'Por favor, informe o motivo da rejeição do documento.',
        variant: 'error',
      });
      return;
    }
    verifyMutation.mutate({
      documentId: docId,
      status: 'REJECTED',
      reason: rejectionReason.trim(),
    });
  };

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return '—';
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateStr));
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800">
            <Check className="h-3 w-3" /> Aprovado
          </span>
        );
      case 'REJECTED':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-300 border border-red-300 dark:border-red-800">
            <Ban className="h-3 w-3" /> Rejeitado
          </span>
        );
      case 'PENDING':
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300 border border-amber-300 dark:border-amber-800">
            <AlertCircle className="h-3 w-3" /> Pendente
          </span>
        );
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0">
        <DialogHeader className="p-6 pb-4 border-b border-border bg-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <DialogTitle className="text-xl font-bold flex items-center gap-2">
                <FileText className="h-6 w-6 text-primary" />
                Documentos e Validação Cadastral
              </DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground mt-1">
                Revise os documentos enviados pelo profissional para validação médica.
              </DialogDescription>
            </div>
          </div>

          {/* Doctor Info Card */}
          <div className="mt-4 p-4 rounded-xl bg-muted/40 border border-border/80 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 text-primary font-bold text-lg flex items-center justify-center border border-primary/20">
                {doctor.name.charAt(0).toUpperCase()}
              </div>
              <div>
                <h3 className="font-semibold text-foreground text-base leading-tight">
                  {doctor.name}
                </h3>
                {doctor.email && (
                  <p className="text-xs text-muted-foreground mt-0.5">{doctor.email}</p>
                )}
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className="text-xs px-2 py-0.5 rounded bg-background border font-mono font-medium text-foreground">
                    CRM: {crmNumber} - {crmUf}
                  </span>
                  {doctor.specialty && (
                    <span className="text-xs px-2 py-0.5 rounded bg-background border text-muted-foreground">
                      {doctor.specialty}
                    </span>
                  )}
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      doctor.status === 'APPROVED'
                        ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
                        : doctor.status === 'PENDING'
                        ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
                        : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
                    }`}
                  >
                    Status: {doctor.status}
                  </span>
                </div>
              </div>
            </div>

            {/* Automatic CRM Verification Badge */}
            <div className="flex flex-col sm:items-end gap-1.5 border-t sm:border-t-0 pt-3 sm:pt-0">
              <span className="text-xs text-muted-foreground font-medium">Validação CRM (CFM)</span>
              {verification?.crmVerified ? (
                <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 px-3 py-1.5 rounded-lg border border-emerald-200 dark:border-emerald-800">
                  <CheckCircle2 className="h-4 w-4" />
                  <span>CRM Validado Automaticamente</span>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 px-3 py-1.5 rounded-lg border border-amber-200 dark:border-amber-800">
                  <AlertCircle className="h-4 w-4" />
                  <span>Validação Automática Pendente</span>
                </div>
              )}
              {verification?.crmVerifiedAt && (
                <span className="text-[11px] text-muted-foreground">
                  Validado em: {formatDate(verification.crmVerifiedAt)}
                </span>
              )}
            </div>
          </div>
        </DialogHeader>

        {/* Modal Body: Documents List */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm font-medium">Carregando documentos do médico...</p>
            </div>
          ) : error ? (
            <div className="p-6 text-center space-y-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900 rounded-xl">
              <AlertTriangle className="h-8 w-8 text-red-600 mx-auto" />
              <p className="text-sm font-medium text-red-800 dark:text-red-300">
                Não foi possível carregar os documentos deste médico.
              </p>
              <button
                onClick={() => refetch()}
                className="px-4 py-2 text-xs font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors"
              >
                Tentar novamente
              </button>
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-16 px-4 border border-dashed rounded-xl space-y-3 bg-muted/20">
              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto text-muted-foreground">
                <FileText className="h-6 w-6" />
              </div>
              <h4 className="font-semibold text-foreground">Nenhum documento anexado</h4>
              <p className="text-xs text-muted-foreground max-w-sm mx-auto">
                Este profissional ainda não realizou o upload de nenhum documento de habilitação
                (CRM, Diploma, Identidade ou RQE).
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between pb-1">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Documentos Enviados ({documents.length})
                </span>
                {verification?.documentsVerified && (
                  <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-medium">
                    <ShieldCheck className="h-4 w-4" /> Todos essenciais aprovados
                  </span>
                )}
              </div>

              {documents.map((doc) => {
                const config =
                  DOCUMENT_TYPE_CONFIG[doc.type] || {
                    label: `Documento (${doc.type})`,
                    description: 'Documento médico complementar',
                    icon: FileText,
                  };
                const Icon = config.icon;
                const fileUrl = filesApi.getFileUrl(doc.fileUrl);
                const isRejectingThis = rejectingDocId === doc.id;
                const isMutatingThis =
                  verifyMutation.isPending && activeMutatingId === doc.id;

                return (
                  <div
                    key={doc.id}
                    className="p-4 rounded-xl border border-border bg-card hover:border-primary/30 transition-all shadow-sm space-y-3"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex items-start gap-3">
                        <div className="p-2.5 rounded-lg bg-primary/10 text-primary mt-0.5">
                          <Icon className="h-5 w-5" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-semibold text-foreground text-sm">
                              {config.label}
                            </h4>
                            {getStatusBadge(doc.status)}
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {config.description}
                          </p>
                          <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              Enviado em {formatDate(doc.createdAt)}
                            </span>
                            {doc.verifiedAt && (
                              <span className="text-xs">
                                • Verificado em {formatDate(doc.verifiedAt)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Right actions */}
                      <div className="flex items-center gap-2 self-end sm:self-center flex-wrap">
                        {/* View file button */}
                        <a
                          href={fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-200 bg-secondary hover:bg-secondary/80 border border-border rounded-lg transition-colors"
                          title="Visualizar documento em nova aba"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                          <span>Ver Arquivo</span>
                        </a>

                        {/* Approve button */}
                        <button
                          type="button"
                          onClick={() => handleApprove(doc.id)}
                          disabled={isMutatingThis || doc.status === 'APPROVED'}
                          className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                            doc.status === 'APPROVED'
                              ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/40 opacity-60 cursor-not-allowed'
                              : 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm disabled:opacity-50'
                          }`}
                          title="Aprovar documento"
                        >
                          {isMutatingThis ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Check className="h-3.5 w-3.5" />
                          )}
                          <span>Aprovar</span>
                        </button>

                        {/* Reject button */}
                        <button
                          type="button"
                          onClick={() =>
                            isRejectingThis ? handleCancelReject() : handleStartReject(doc.id)
                          }
                          disabled={isMutatingThis}
                          className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${
                            isRejectingThis
                              ? 'bg-muted text-muted-foreground border-border'
                              : 'border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 dark:border-red-800'
                          } disabled:opacity-50`}
                          title="Rejeitar documento"
                        >
                          <Ban className="h-3.5 w-3.5" />
                          <span>{isRejectingThis ? 'Cancelar' : 'Rejeitar'}</span>
                        </button>
                      </div>
                    </div>

                    {/* Rejection Reason Alert if document already rejected */}
                    {doc.status === 'REJECTED' && doc.rejectionReason && !isRejectingThis && (
                      <div className="p-3 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 text-xs text-red-800 dark:text-red-300 flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 shrink-0 text-red-600 dark:text-red-400 mt-0.5" />
                        <div>
                          <span className="font-semibold">Motivo da Rejeição:</span>{' '}
                          {doc.rejectionReason}
                        </div>
                      </div>
                    )}

                    {/* Rejection Form Box when rejecting */}
                    {isRejectingThis && (
                      <div className="mt-3 p-3.5 rounded-lg bg-red-50/70 dark:bg-red-950/20 border border-red-200 dark:border-red-900/60 space-y-2.5 animate-in fade-in duration-200">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-semibold text-red-900 dark:text-red-200 flex items-center gap-1.5">
                            <AlertTriangle className="h-3.5 w-3.5 text-red-600" />
                            Informe o motivo da rejeição deste documento:
                          </label>
                        </div>

                        {/* Quick preset chips */}
                        <div className="flex flex-wrap gap-1.5">
                          {COMMON_REJECTION_REASONS.map((preset) => (
                            <button
                              key={preset}
                              type="button"
                              onClick={() => setRejectionReason(preset)}
                              className="text-[11px] px-2 py-0.5 rounded-full bg-white dark:bg-zinc-800 border border-red-200 dark:border-red-800 text-gray-700 dark:text-gray-300 hover:bg-red-100 hover:text-red-800 transition-colors"
                            >
                              {preset}
                            </button>
                          ))}
                        </div>

                        <textarea
                          value={rejectionReason}
                          onChange={(e) => setRejectionReason(e.target.value)}
                          placeholder="Digite aqui a justificativa ou selecione um motivo acima..."
                          rows={2}
                          className="w-full text-xs p-2.5 rounded-lg border border-red-200 dark:border-red-800 bg-white dark:bg-zinc-900 text-foreground focus:outline-none focus:ring-2 focus:ring-red-500 placeholder:text-muted-foreground resize-none"
                        />

                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={handleCancelReject}
                            className="px-3 py-1.5 text-xs font-medium rounded-lg text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-zinc-800 transition-colors"
                          >
                            Voltar
                          </button>
                          <button
                            type="button"
                            onClick={() => handleConfirmReject(doc.id)}
                            disabled={isMutatingThis || !rejectionReason.trim()}
                            className="inline-flex items-center gap-1 px-3.5 py-1.5 text-xs font-semibold rounded-lg bg-red-600 hover:bg-red-700 text-white shadow-sm disabled:opacity-50 transition-colors"
                          >
                            {isMutatingThis ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Ban className="h-3.5 w-3.5" />
                            )}
                            <span>Confirmar Rejeição</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter className="p-4 border-t border-border bg-card flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {doctor.status === 'APPROVED' ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300 border border-emerald-300">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <span>Cadastro Aprovado e Ativo</span>
              </span>
            ) : (
              <span className="text-xs text-muted-foreground font-medium flex items-center gap-1.5">
                <FileCheck className="w-4 h-4 text-primary" />
                <span>
                  Obrigatórios Aprovados: <strong className="text-foreground">{approvedEssentialCount}/3</strong> (CRM, Diploma, RG/CNH)
                </span>
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap justify-end">
            {doctor.status !== 'APPROVED' && (
              <>
                {!isRejectingDoctor ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setIsRejectingDoctor(true)}
                      className="px-3.5 py-2 text-xs font-semibold rounded-xl border border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                    >
                      Recusar Cadastro
                    </button>
                    <button
                      type="button"
                      onClick={() => approveDoctorMutation.mutate()}
                      disabled={approveDoctorMutation.isPending}
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white shadow-md shadow-emerald-600/20 disabled:opacity-50 transition-colors cursor-pointer"
                    >
                      {approveDoctorMutation.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <ShieldCheck className="w-4 h-4" />
                      )}
                      <span>Aprovar Médico e Ativar Conta</span>
                    </button>
                  </>
                ) : (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      placeholder="Motivo da recusa..."
                      value={doctorRejectReason}
                      onChange={(e) => setDoctorRejectReason(e.target.value)}
                      className="px-3 py-1.5 text-xs rounded-xl border border-input bg-background text-foreground"
                    />
                    <button
                      type="button"
                      onClick={() => rejectDoctorMutation.mutate(doctorRejectReason)}
                      disabled={rejectDoctorMutation.isPending}
                      className="px-3 py-1.5 text-xs font-bold rounded-xl bg-red-600 text-white hover:bg-red-700 transition-colors"
                    >
                      Confirmar
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsRejectingDoctor(false)}
                      className="px-2.5 py-1.5 text-xs rounded-xl border border-border"
                    >
                      Cancelar
                    </button>
                  </div>
                )}
              </>
            )}
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-medium rounded-xl bg-secondary hover:bg-secondary/80 text-foreground border border-border transition-colors cursor-pointer"
            >
              Fechar
            </button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
